import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  validateResetToken: (token) => api.get(`/auth/validate-reset-token?token=${token}`),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Releases API (collection with releases + copies)
export const releasesAPI = {
  getCollection: (params) => api.get('/releases', { params }),
  getFilters: () => api.get('/releases/filters'),
  getRelease: (discogsReleaseId) => api.get(`/releases/${discogsReleaseId}`),
  checkExisting: (discogsReleaseId) => api.get('/releases/check-existing', { params: { discogs_release_id: discogsReleaseId } }),
  refreshStats: (discogsReleaseId) => api.post(`/releases/${discogsReleaseId}/refresh-stats`),
  refreshAllStats: () => api.post('/releases/refresh-all-stats'),
  updateRelease: (id, data) => api.patch(`/releases/${id}`, data),
};

// Export API
export const exportAPI = {
  downloadCSV: () => api.get('/export/csv', { responseType: 'blob' }),
  downloadJSON: () => api.get('/export/json', { responseType: 'blob' }),
};

// Wishlist API
export const wishlistAPI = {
  getAll: (params) => api.get('/wishlist', { params }),
  getStats: () => api.get('/wishlist/stats'),
  add: (itemData) => api.post('/wishlist', itemData),
  remove: (itemId) => api.delete(`/wishlist/${itemId}`),
  moveToCollection: (itemId, condition_vinyl = 'VG+', condition_cover = 'VG+') => 
    api.post(`/wishlist/${itemId}/move-to-collection`, null, { 
      params: { condition_vinyl, condition_cover } 
    }),
};

// Copies API
export const copiesAPI = {
  add: (copyData, releaseData) => api.post('/copies', { 
    discogs_release_id: copyData.discogs_release_id,
    condition_vinyl: copyData.condition_vinyl || 'VG+',
    condition_cover: copyData.condition_cover || 'VG+',
    purchase_price: copyData.purchase_price || null,
    purchase_date: copyData.purchase_date || null,
    notes: copyData.notes || null,
    location: copyData.location || null,
    release_data: releaseData 
  }),
  batchAdd: (items, defaultConditionVinyl = 'VG+', defaultConditionCover = 'VG+') => 
    api.post('/copies/batch', {
      items,
      default_condition_vinyl: defaultConditionVinyl,
      default_condition_cover: defaultConditionCover
    }),
  get: (copyId) => api.get(`/copies/${copyId}`),
  update: (copyId, data) => api.put(`/copies/${copyId}`, data),
  delete: (copyId) => api.delete(`/copies/${copyId}`),
  // For Sale features (PRO)
  updateForSale: (copyId, forSale, salePrice, saleNote = null) => 
    api.put(`/copies/${copyId}/for-sale`, { for_sale: forSale, sale_price: salePrice, sale_note: saleNote }),
  getForSale: () => api.get('/copies/for-sale/all'),
};

// Sale List API (PRO)
export const saleListAPI = {
  ensure: () => api.post('/sale-list/ensure'),
  update: (data) => api.patch('/sale-list', data),
  rotateToken: () => api.post('/sale-list/rotate-token'),
};

// Public API (no auth required)
export const publicAPI = {
  getSaleList: (token) => axios.get(`${API_URL}/api/public/sale-list/${token}`),
  // Legacy
  getForSale: (userId) => axios.get(`${API_URL}/api/public/for-sale/${userId}`),
};

// Discogs API
export const discogsAPI = {
  search: (params) => api.get('/discogs/search', { params }),
  matrixSearch: (matrixCode, artist = null) => api.post('/discogs/matrix-search', { matrix_code: matrixCode, artist }),
  getRelease: (id) => api.get(`/discogs/release/${id}`),
  getMaster: (id) => api.get(`/discogs/master/${id}`),
  getMasterVersions: (id, params = {}) => api.get(`/discogs/master/${id}/versions`, { params }),
  extractFromImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/discogs/ai-extract', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  extractMatrixFromImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/discogs/ai-matrix-ocr', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Health API
export const healthAPI = {
  checkDiscogs: () => api.get('/health/discogs'),
};

// Stats API
export const statsAPI = {
  getStats: () => api.get('/stats'),
};

// Migration API
export const migrationAPI = {
  migrate: () => api.post('/migrate'),
};

// Backup API
export const backupAPI = {
  sendEmailBackup: () => api.post('/backup/email'),
  restoreBackup: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/backup/restore', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Billing API
export const billingAPI = {
  getPricing: () => api.get('/billing/pricing'),
  getSubscription: () => api.get('/billing/subscription'),
  createCheckout: (priceId) => api.post('/billing/create-checkout', { price_id: priceId }),
  cancelSubscription: () => api.post('/billing/cancel-subscription'),
  changePlan: (newPlanId) => api.post('/billing/change-plan', { new_plan_id: newPlanId }),
};

// Uploads API
export const uploadsAPI = {
  uploadCover: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/uploads/cover', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Admin API
export const adminAPI = {
  getUsers: (adminSecret, skip = 0, limit = 100) => 
    api.get('/auth/admin/users', { 
      headers: { 'X-Admin-Secret': adminSecret },
      params: { skip, limit }
    }),
  getStats: (adminSecret) => 
    api.get('/auth/admin/stats', { 
      headers: { 'X-Admin-Secret': adminSecret }
    }),
  getDashboard: (adminSecret, startDate, endDate) => 
    api.get('/auth/admin/dashboard', { 
      headers: { 'X-Admin-Secret': adminSecret },
      params: { start_date: startDate, end_date: endDate }
    }),
  setUserPlan: (adminSecret, email, plan) => 
    api.post('/auth/admin/set-plan', 
      { email, plan },
      { headers: { 'X-Admin-Secret': adminSecret }}
    ),
  deleteUser: (adminSecret, userId) => 
    api.delete(`/auth/admin/users/${userId}`, { 
      headers: { 'X-Admin-Secret': adminSecret }
    }),
};

export default api;
